import {
  getSettings, putSettings, addContribution, addWithdrawal,
  type HBFDatabase,
} from './db';
import type { AppState, WizardInputs } from './state';
import type { ExportPayload } from './export';
import type { ResidencyYear, CRARoomMethod } from '../engine/types';

type ParseResult =
  | { ok: true; payload: ExportPayload }
  | { ok: false; error: string };

export type ImportResult =
  | { ok: true; state: AppState }
  | { ok: false; error: string };

export function parseExport(json: string): ParseResult {
  try {
    const parsed = JSON.parse(json);
    if (parsed?.format !== 'hbf-tfsa-export') {
      return { ok: false, error: 'Invalid format: expected hbf-tfsa-export' };
    }
    if (parsed?.format_version !== 1) {
      return { ok: false, error: `Unsupported format_version: ${parsed?.format_version}` };
    }
    if (!parsed?.settings?.birth_year || !parsed?.settings?.residency_year) {
      return { ok: false, error: 'Invalid export: missing required settings fields' };
    }
    return { ok: true, payload: parsed as ExportPayload };
  } catch {
    return { ok: false, error: 'Invalid JSON — file may be corrupt or truncated' };
  }
}

export async function applyImport(db: HBFDatabase, payload: ExportPayload): Promise<ImportResult> {
  // REFUSE if any data already exists
  const existing = await getSettings(db);
  if (existing !== null) {
    return {
      ok: false,
      error: 'Data already exists. Go to Settings → Reset all data before importing.',
    };
  }

  const s = payload.settings;

  await putSettings(db, {
    birth_year: s.birth_year,
    residency_year: s.residency_year as ResidencyYear,
    cra_room_initial_cents: s.cra_room_initial_cents,
    cra_room_method: s.cra_room_method as CRARoomMethod,
    cra_verified_at_iso: s.cra_verified_at_iso,
    contributions_ytd_cents: s.contributions_ytd_cents,
    withdrawals_ytd_cents: s.withdrawals_ytd_cents,
    wizard_completed_at: s.wizard_completed_at,
    disclaimer_accepted_at: s.disclaimer_accepted_at,
    last_backup_export_at: null,
    contributions_since_last_backup: 0,
  });

  const logEntries: AppState['logEntries'] = [];

  for (const c of payload.contributions) {
    const id = await addContribution(db, {
      date: c.date,
      amount_cents: c.amount_cents,
      ...(c.institution ? { institution: c.institution } : {}),
      created_at: c.created_at,
    });
    logEntries.push({
      id,
      type: 'contribution',
      date: c.date,
      amount_cents: c.amount_cents,
      ...(c.institution ? { institution: c.institution } : {}),
      created_at: c.created_at,
    });
  }

  for (const w of payload.withdrawals) {
    const id = await addWithdrawal(db, {
      date: w.date,
      amount_cents: w.amount_cents,
      ...(w.institution ? { institution: w.institution } : {}),
      created_at: w.created_at,
    });
    logEntries.push({
      id,
      type: 'withdrawal',
      date: w.date,
      amount_cents: w.amount_cents,
      ...(w.institution ? { institution: w.institution } : {}),
      created_at: w.created_at,
    });
  }

  logEntries.sort((a, b) => a.created_at.localeCompare(b.created_at));

  const wizardInputs: WizardInputs | null = s.wizard_completed_at ? {
    birth_year: s.birth_year,
    residency_year: s.residency_year as ResidencyYear,
    cra_room_method: s.cra_room_method as CRARoomMethod,
    cra_room_initial_cents: s.cra_room_initial_cents,
    cra_verified_at_iso: s.cra_verified_at_iso,
    contributions_ytd_cents: s.contributions_ytd_cents,
    withdrawals_ytd_cents: s.withdrawals_ytd_cents,
  } : null;

  return {
    ok: true,
    state: {
      disclaimerAccepted: !!s.disclaimer_accepted_at,
      wizardCompleted: !!s.wizard_completed_at,
      wizardInputs,
      logEntries,
      constantsVersion: payload.constants_version,
      constantsVerifiedAt: s.cra_verified_at_iso?.split('T')[0] ?? '',
    },
  };
}
